import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { LanguageProvider } from "./i18n/LanguageContext.tsx";
import AppPreloader from "./components/layout/AppPreloader";
import { AuthProvider } from "./hooks/useAuth.tsx";
import "./lib/sentry";
import "./index.css";

// ── Chunk recovery: after a deploy, old chunk URLs 404. Reload once to get fresh HTML. ──
window.addEventListener("vite:preloadError", (e) => {
  e.preventDefault();
  const reloaded = sessionStorage.getItem("chunk-reload");
  if (!reloaded) {
    sessionStorage.setItem("chunk-reload", "1");
    window.location.reload();
  }
});
// Clear the flag on successful load so future deploys can also recover
sessionStorage.removeItem("chunk-reload");

/**
 * Register the real service worker (production only — a worker that caches
 * assets during development serves yesterday's bundle).
 *
 * This has to happen on load and not only when the user subscribes to push:
 * `navigator.serviceWorker.ready` never resolves while nothing is registered,
 * and the push hook used to await it.
 */
const registerServiceWorker = () => {
  try {
    if (!import.meta.env.PROD) return;
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch(() => undefined);
    });
  } catch {
    // A worker is an enhancement; the app works without one.
  }
};

const runLegacyServiceWorkerCleanup = () => {
  // Never block first paint/hydration on cleanup logic.
  // Also guard browser APIs that may be unavailable/restricted in some environments.
  try {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

    const safeSessionStorage = (() => {
      try {
        return window.sessionStorage;
      } catch {
        return null;
      }
    })();

    if (!safeSessionStorage || safeSessionStorage.getItem("sw-cleaned")) return;

    navigator.serviceWorker.getRegistrations()
      .then((regs) => {
        regs.forEach((reg) => {
          const isLegacy =
            reg.active?.scriptURL?.includes("registerSW") ||
            reg.active?.scriptURL?.includes("sw.js?v=") ||
            reg.active?.scriptURL?.includes("workbox");
          if (isLegacy) reg.unregister();
        });
      })
      .catch(() => undefined);

    if ("caches" in window) {
      window.caches.keys()
        .then((keys) => {
          keys.forEach((k) => {
            // Anything the current worker does not own is a leftover from a
            // previous strategy. A name list goes stale; the prefix is the
            // worker's contract (`ky-`, see public/sw.js).
            if (!k.startsWith("ky-")) {
              window.caches.delete(k);
            }
          });
        })
        .catch(() => undefined);
    }

    safeSessionStorage.setItem("sw-cleaned", "1");
  } catch {
    // Silently ignore cleanup failures to avoid breaking app bootstrap.
  }
};

createRoot(document.getElementById("root")!).render(
  <>
    {/* Boot overlay sits OUTSIDE LanguageProvider: the provider gates its
        children until the first locale chunk resolves, and the preloader must
        stay visible across that window. */}
    <AppPreloader />
    <LanguageProvider>
      <AuthProvider>
        <App />
      </AuthProvider>
    </LanguageProvider>
  </>
);

// Note: the boot screen is rendered by React (AppPreloader), so there is no
// inline #initial-preloader element in index.html to remove here.

queueMicrotask(runLegacyServiceWorkerCleanup);
registerServiceWorker();
